import type { TFunction } from 'i18next'
import { reportClientError } from 'utils/reportClientError'
import type { Venue } from './types'

/**
 * Every way a swap can fail, said as a sentence.
 *
 * Scope 07's rule applies here more than anywhere: a swap fails in public, on
 * the user's money, and a hex revert string tells them nothing they can act
 * on. Each entry below names one situation and what to do about it; anything
 * that matches nothing is reported unhandled, because a failure we cannot name
 * is a gap in this list rather than an acceptable outcome.
 */

export interface SwapErrorContext {
  /** The venue whose route was being executed, when one was. */
  venue?: Venue
  /** Whether the token being sold is MPRO. */
  isMproIn?: boolean
  /** MPRO's burn for this account, in basis points. */
  burnBps?: number
}

interface DecoderEntry {
  key: string
  /** Substrings, matched case-insensitively. */
  match?: string[]
  /**
   * Short revert codes, matched on word boundaries. `TF`, `IIA` and `STF` are
   * two and three letters long: as plain substrings they hit "utf-8" and
   * anything else that happens to contain them, and a misclassified failure
   * hands the user advice for a problem they do not have.
   */
  codes?: string[]
  /** When present, the entry only applies if this says so. */
  when?: (ctx: SwapErrorContext) => boolean
}

/** Concentrated liquidity cannot deliver a token that shrinks in transit. */
function isFeeOnTransferOnConcentratedRoute(ctx: SwapErrorContext): boolean {
  const concentrated = ctx.venue === 'uni-v3' || ctx.venue === 'aero-cl'
  return concentrated && Boolean(ctx.isMproIn) && (ctx.burnBps ?? 0) > 0
}

/**
 * Order is meaning, not preference. A wallet rejection is checked first
 * because it is not a failure at all, and the narrow, context-gated entries
 * come before the broad ones they would otherwise be swallowed by.
 */
export const ENTRIES: DecoderEntry[] = [
  { key: 'userRejected', match: ['user rejected', 'action_rejected', 'user denied', 'rejected the request'] },
  { key: 'insufficientGas', match: ['insufficient funds for gas', 'insufficient funds'] },
  {
    key: 'feeOnTransferUnsupported',
    codes: ['iia', 'tf'],
    when: isFeeOnTransferOnConcentratedRoute
  },
  { key: 'transferBlocked', match: ['transferallowed', 'blocklist', 'blocked'] },
  {
    key: 'slippage',
    // `router:insufficientoutput` is the platform router re-checking the floor
    // the venue already enforces (scope 16) — the same advice applies.
    match: [
      'insufficient_output_amount',
      'too little received',
      'insufficient output amount',
      'router:insufficientoutput'
    ]
  },
  { key: 'expired', match: ['expired', 'transaction too old'] },
  /*
   * `STF` is TransferHelper's safeTransferFrom refusing — an allowance or a
   * balance, never a price move. Scope 13 §7 lists it under both `slippage`
   * and `allowance`, discriminated by "route context"; it is recorded as
   * allowance here, and only here, because the two entries give opposite
   * advice and only one of them can be acted on. Telling somebody to raise
   * their slippage when their approval is short sends them round the loop
   * again with the same result.
   */
  {
    key: 'allowance',
    match: ['transfer_from_failed', 'erc20: insufficient allowance'],
    codes: ['stf']
  },
  { key: 'quoteStale', match: ['swap:quote-stale'] },
  /*
   * `missing revert data` / `reverted without a reason string`, with
   * `data="0x"`, is what a simulation that produced nothing at all looks like
   * through ethers — and it has two very different causes that cannot be told
   * apart from the browser: a route that genuinely cannot fill (a pool too
   * thin for this size), and a public node refusing the `eth_call` outright.
   * Our own gateway is built on that second reading: a quota-exhausted
   * provider once reached ethers in exactly this shape and a user was told
   * their transaction had reverted when nothing had.
   *
   * So the sentence names both and asks for the one action that helps either
   * way — a smaller size, or another try. It sits below every entry that can
   * identify a real cause, because it is the broadest match in the list.
   */
  {
    key: 'simulationEmpty',
    match: [
      'missing revert data',
      'reverted without a reason string',
      'cannot estimate gas',
      'unpredictable_gas_limit'
    ]
  },
  /*
   * The platform router's own refusals (scope 16), named by the execution
   * hook from their custom-error selectors. `InsufficientOutput` joins the
   * slippage entry above; `Expired` matches the expired entry.
   */
  { key: 'routerTarget', match: ['router:targetnotallowed'] },
  { key: 'routerPaused', match: ['router:enforcedpause'] },
  {
    key: 'routerBadRequest',
    match: ['router:badvalue', 'router:badrecipient', 'router:sametoken', 'router:zeroamount']
  }
]

function rawTextOf(err: unknown): string {
  if (err instanceof Error) {
    const data = (err as { data?: { message?: unknown } }).data
    const extra = data && typeof data.message === 'string' ? ` ${data.message}` : ''
    const code = (err as { code?: unknown }).code
    const codeText = code === 4001 || code === 'ACTION_REJECTED' ? ' user rejected' : ''
    return `${err.message}${extra}${codeText}`
  }
  if (err && typeof err === 'object') {
    const code = (err as { code?: unknown }).code
    const message = (err as { message?: unknown }).message
    const codeText = code === 4001 || code === 'ACTION_REJECTED' ? ' user rejected' : ''
    return `${typeof message === 'string' ? message : JSON.stringify(err)}${codeText}`
  }
  return String(err)
}

/** The entry a raw error belongs to, or `null`. Exported so tests can be honest. */
export function classifySwapError(err: unknown, ctx: SwapErrorContext = {}): string | null {
  const raw = rawTextOf(err).toLowerCase()
  for (const entry of ENTRIES) {
    if (entry.when && !entry.when(ctx)) continue
    if (entry.match?.some((m) => raw.includes(m))) return entry.key
    if (entry.codes?.some((code) => new RegExp(`(^|[^a-z0-9])${code}([^a-z0-9]|$)`).test(raw))) {
      return entry.key
    }
  }
  return null
}

/** Turns any failure into the sentence the user reads, and records what it was. */
export function decodeSwapError(err: unknown, t: TFunction, ctx: SwapErrorContext = {}): string {
  const raw = rawTextOf(err)
  const key = classifySwapError(err, ctx)

  if (key) {
    // A wallet rejection is a decision, not a fault; recording it would drown
    // the signal observability exists to carry.
    if (key !== 'userRejected') {
      reportClientError({
        message: `swap.${key}: ${raw.slice(0, 300)}`,
        kind: 'transaction',
        handled: true
      })
    }
    return t(`swap.errors.${key}`)
  }

  /*
   * Recorded in full, shown as a sentence.
   *
   * `{{reason}}` used to carry `raw`, and `raw` is the whole ethers error —
   * including the serialised transaction. On 2026-09-09 a user attempting a
   * swap read a toast that began "The swap failed: missing revert data in call
   * exception … (data="0x", transaction={"from":"0x5701…" and continued into
   * JSON. Nothing in that is actionable, and the part that identifies them is
   * the part they did not need to see. The detail belongs in observability,
   * which is where it now goes and nowhere else.
   */
  reportClientError({
    message: `swap.unknown: ${raw.slice(0, 300)}`,
    kind: 'transaction',
    handled: false
  })
  return t('swap.errors.unknown')
}

/** Raised by the execution hook when the dry run returns less than the floor. */
export class QuoteStaleError extends Error {
  constructor() {
    super('swap:quote-stale')
    this.name = 'QuoteStaleError'
  }
}

/**
 * Run a simulation again when the answer was empty rather than a refusal.
 *
 * Measured 2026-09-10. A wallet's ETH→MPRO swap failed on `signer.call` with
 * `missing revert data … data="0x"`, and the platform told them the swap had
 * failed. It had not: replaying the identical transaction from the same address
 * — same router, same venue, same size — returns **63,902 MPRO** and a 25 bps
 * fee. Nothing was wrong with the route, the pool or the contract. A public
 * node had declined the `eth_call`, and an empty answer is indistinguishable
 * from a revert with no reason.
 *
 * The gateway rotates providers per request, so the retry lands somewhere else.
 * One retry, and only for the empty shape: a revert that carries a reason is a
 * real answer from the chain and must reach the user unchanged, immediately.
 */
export async function retryOnEmptySimulation<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run()
  } catch (error) {
    if (classifySwapError(error) !== 'simulationEmpty') throw error
    return run()
  }
}
